/**
 * Drive to Store (DTS) — 3-Step Wizard
 *
 * Step 1: Store selection — Leaflet + OpenStreetMap, locations fetched from
 *   the "dts_customer" metaobject via the Storefront API (all locations,
 *   clustered; carries sap_id/partner_id needed by the submission payload).
 * Step 2: Calendar date picker (pure JS, no library)
 * Step 3: Customer form + submission
 */

(function () {
  'use strict';

  // ── Config ───────────────────────────────────────────────────────────────
  // Cambiar a false cuando la app este en Azure
  const DTS_MOCK_MODE = false;

  const DTS_LIMITS = { MAX_OPTICAL: 3, MAX_SUN: 1 };

  const SHOPFINDER_CACHE_TTL = 2 * 3600 * 1000; // 2h, matches ff-shopfinder pattern

  const pinIcon = (typeof L !== 'undefined') ? L.divIcon({
    className: '',
    html: '<svg width="24" height="36" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24S24 21 24 12C24 5.373 18.627 0 12 0z" fill="#1a1a1a"/><circle cx="12" cy="12" r="4.5" fill="#fff"/></svg>',
    iconSize: [24, 36],
    iconAnchor: [12, 36],
    popupAnchor: [0, -38],
  }) : null;

  // ── State ────────────────────────────────────────────────────────────────

  let map;
  let clusterer;
  let allLocations = [];

  let state = {
    currentStep: 1,
    selectedStore: null,
    selectedDate: null,
    calendarMonth: new Date().getMonth(),
    calendarYear: new Date().getFullYear(),
    cartItems: [],
  };

  // ── Init ──────────────────────────────────────────────────────────────────

  function init() {
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return;

    // Read cart data from JSON script tag
    const cartDataEl = document.querySelector('[data-ff-dts-cart-data]');
    if (cartDataEl) {
      try {
        state.cartItems = JSON.parse(cartDataEl.textContent);
      } catch (err) {
        console.error('[DTS] Error parsing cart data:', err);
        state.cartItems = [];
      }
    }

    initMap();
    initCalendar();
    initSteps();
    initForm();
  }

  // Espera a que Leaflet cargue, luego inicializa
  function waitForLeaflet() {
    if (typeof L !== 'undefined' && L.map) {
      init();
    } else {
      setTimeout(waitForLeaflet, 100);
    }
  }

  // Arrancar cuando el DOM este listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForLeaflet);
  } else {
    waitForLeaflet();
  }

  // ── Map (Step 1) ─────────────────────────────────────────────────────────

  function initMap() {
    const mapEl = document.getElementById('ff-dts-map');
    if (!mapEl) return;

    const shopDomain = mapEl.dataset.shopDomain || '';
    const token = mapEl.dataset.storefrontToken || '';
    const mapApiKey = mapEl.dataset.mapApiKey || '';
    if (!token) {
      console.warn('[DTS] Missing Storefront API token — cannot load store locations');
      return;
    }

    map = L.map(mapEl, { zoomControl: true }).setView([41.3851, 2.1734], 12);

    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png' + (mapApiKey ? '?key=' + encodeURIComponent(mapApiKey) : ''), {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    setTimeout(() => map.invalidateSize(), 200);

    initSearch();
    loadAllLocations(shopDomain, token);
  }

  // ── Load dts_customer metaobjects (Storefront API, paginated) ────────────

  const DTS_CACHE_KEY_PREFIX = 'ff_dts_customer_v1_';

  function loadLocationsFromCache(shopDomain) {
    try {
      const raw = localStorage.getItem(DTS_CACHE_KEY_PREFIX + shopDomain);
      if (!raw) return null;
      const entry = JSON.parse(raw);
      if (Date.now() - entry.ts > SHOPFINDER_CACHE_TTL) return null;
      return entry.data;
    } catch (e) { return null; }
  }

  function saveLocationsToCache(shopDomain, locations) {
    try {
      localStorage.setItem(DTS_CACHE_KEY_PREFIX + shopDomain, JSON.stringify({ ts: Date.now(), data: locations }));
    } catch (e) {}
  }

  async function fetchAllLocations(shopDomain, token, onPage) {
    const locations = [];
    let hasNextPage = true;
    let cursor = null;
    let isFirst = true;

    while (hasNextPage) {
      const after = cursor ? `, after: "${cursor}"` : '';
      const q = `{ metaobjects(type: "dts_customer", first: 250${after}) { nodes { id handle fields { key value } } pageInfo { hasNextPage endCursor } } }`;

      const res = await fetch(`https://${shopDomain}/api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: q }),
      });

      const json = await res.json();
      const page = json.data.metaobjects;

      page.nodes.forEach((node) => {
        const f = {};
        node.fields.forEach((field) => { f[field.key] = field.value; });
        const lat = parseFloat(f.latitude);
        const lng = parseFloat(f.longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          locations.push({
            handle: node.handle,
            name: f.name || '',
            sapId: f.sap_id || '',
            partnerId: f.partner_id || '',
            street: f.address || '',
            zip: f.zip_code || '',
            city: f.city || '',
            country: f.country || '',
            lat: lat,
            lng: lng,
          });
        }
      });

      onPage(locations, isFirst);
      isFirst = false;

      hasNextPage = page.pageInfo.hasNextPage;
      cursor = page.pageInfo.endCursor;
    }

    return locations;
  }

  async function loadAllLocations(shopDomain, token) {
    const cached = loadLocationsFromCache(shopDomain);
    if (cached) {
      allLocations = cached;
      buildMarkers(allLocations, false);
      return;
    }

    try {
      const locations = await fetchAllLocations(shopDomain, token, (partial) => {
        allLocations = partial;
        buildMarkers(allLocations, false);
      });
      allLocations = locations;
      buildMarkers(allLocations, false);
      saveLocationsToCache(shopDomain, locations);
    } catch (err) {
      console.error('[DTS] Error loading dts_customer metaobjects:', err);
    }
  }

  // ── Render markers (Leaflet + markercluster) ─────────────────────────────

  function buildMarkers(locations, fitMap) {
    if (clusterer) map.removeLayer(clusterer);
    clusterer = L.markerClusterGroup({
      maxClusterRadius: 50,
      iconCreateFunction: (cluster) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div><span>${count}</span></div>`,
          className: 'marker-cluster marker-cluster-' + (count < 10 ? 'small' : count < 100 ? 'medium' : 'large'),
          iconSize: L.point(40, 40),
        });
      },
    });

    locations.forEach((loc) => {
      const marker = L.marker([loc.lat, loc.lng], { icon: pinIcon });
      marker.on('click', () => onLocationClick(loc));
      clusterer.addLayer(marker);
    });

    map.addLayer(clusterer);

    if (fitMap && locations.length > 0) {
      map.fitBounds(clusterer.getBounds().pad(0.05));
    }
  }

  // ── Marker click → store detail card ─────────────────────────────────────

  function onLocationClick(loc) {
    map.panTo([loc.lat, loc.lng]);
    renderStoreCard(loc);
  }

  let _storePanelClosing = false;

  function renderStoreCard(store) {
    const cardContainer = document.querySelector('[data-ff-dts-store-card]');
    if (!cardContainer) return;
    if (_storePanelClosing) return;

    cardContainer.innerHTML = `
      <button type="button" class="ff-dts__store-card-close" data-ff-dts-store-card-close aria-label="${escapeAttr(getTranslation('close') || 'Close')}">&#x2715;</button>
      <div class="ff-dts__store-card-info">
        <p class="ff-dts__store-card-name ff-snippet-text"><strong>${escapeHTML(store.name)}</strong></p>
        <p class="ff-dts__store-card-address ff-snippet-text">
          ${escapeHTML(store.street)}<br>
          ${escapeHTML(store.zip)} ${escapeHTML(store.city)}, ${escapeHTML(store.country)}
        </p>
      </div>
      <button
        class="ff-snippet-button ff-dts-action-btn"
        type="button"
        data-ff-dts-confirm="1"
        data-store-handle="${escapeAttr(store.handle)}"
        data-store-name="${escapeAttr(store.name)}"
        data-store-sapid="${escapeAttr(store.sapId)}"
        data-store-partnerid="${escapeAttr(store.partnerId)}"
        data-store-street="${escapeAttr(store.street)}"
        data-store-city="${escapeAttr(store.city)}"
        data-store-zip="${escapeAttr(store.zip)}"
        data-store-country="${escapeAttr(store.country)}"
        data-store-lat="${escapeAttr(store.lat)}"
        data-store-lng="${escapeAttr(store.lng)}"
      >
        <span class="ff-button-text">
          <div class="ff-dts-action-btn-text ff-snippet-text">
            ${escapeHTML(getTranslation('confirmStore'))}
          </div>
        </span>
      </button>
    `;

    cardContainer.classList.add('is-open');

    const closeBtn = cardContainer.querySelector('[data-ff-dts-store-card-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        _storePanelClosing = true;
        cardContainer.classList.remove('is-open');
        setTimeout(() => { _storePanelClosing = false; }, 350);
      });
    }

    // Bind confirm button
    const confirmBtn = cardContainer.querySelector('[data-ff-dts-confirm="1"]');
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => {
        state.selectedStore = {
          hash: confirmBtn.dataset.storeHandle,
          name: confirmBtn.dataset.storeName,
          sap_id: confirmBtn.dataset.storeSapid,
          partner_id: confirmBtn.dataset.storePartnerid,
          street: confirmBtn.dataset.storeStreet,
          city: confirmBtn.dataset.storeCity,
          zip: confirmBtn.dataset.storeZip,
          country: confirmBtn.dataset.storeCountry,
          lat: parseFloat(confirmBtn.dataset.storeLat) || null,
          lng: parseFloat(confirmBtn.dataset.storeLng) || null,
        };
        completeStep(1);
      });
    }
  }

  // ── Search (token match across loaded locations, with geocoding fallback) ─

  function initSearch() {
    const input = document.getElementById('ff-dts-search-input');
    if (!input) return;

    let searchTimer = null;
    input.addEventListener('input', function () {
      clearTimeout(searchTimer);
      const query = this.value.trim();
      searchTimer = setTimeout(async () => {
        if (!query) { buildMarkers(allLocations, false); return; }
        let results = tokenSearch(query);
        if (!results.length && query.length >= 5) {
          results = await proximitySearch(query, 100);
        }
        buildMarkers(results, results.length > 0);
      }, 600);
    });
  }

  function tokenSearch(query) {
    const tokens = query.toLowerCase()
      .split(/[\s,.\/-]+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 3);

    if (!tokens.length) return allLocations;

    const scored = [];
    allLocations.forEach((l) => {
      const haystack = `${l.name} ${l.city} ${l.country} ${l.street} ${l.zip}`.toLowerCase();
      let score = 0;
      tokens.forEach((token) => { if (haystack.indexOf(token) !== -1) score++; });
      if (score > 0) scored.push({ loc: l, score });
    });

    scored.sort((a, b) => b.score - a.score);
    return scored.map((s) => s.loc);
  }

  function haversineKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  async function proximitySearch(query, radiusKm) {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(query)}`,
        { headers: { 'Accept-Language': 'en' } }
      );
      const data = await res.json();
      if (!data.length) return [];
      const lat = parseFloat(data[0].lat);
      const lng = parseFloat(data[0].lon);

      return allLocations
        .map((l) => ({ loc: l, dist: haversineKm(lat, lng, l.lat, l.lng) }))
        .filter((s) => s.dist <= radiusKm)
        .sort((a, b) => a.dist - b.dist)
        .map((s) => s.loc);
    } catch (e) { return []; }
  }

  // ── Near me button ───────────────────────────────────────────────────────

  function initNearMe() {
    const btn = document.querySelector('[data-ff-dts-near-me]');
    if (!btn || !navigator.geolocation) {
      if (btn) btn.style.display = 'none';
      return;
    }

    btn.addEventListener('click', () => {
      btn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          map.panTo({ lat: coords.latitude, lng: coords.longitude });
          map.setZoom(12);
          btn.disabled = false;
        },
        () => {
          console.warn('[DTS] Geolocation permission denied');
          btn.disabled = false;
        }
      );
    });
  }

  // ── Calendar (Step 2) ────────────────────────────────────────────────────

  function initCalendar() {
    const calendarEl = document.querySelector('[data-ff-dts-calendar]');
    if (!calendarEl) return;

    renderCalendar();

    // Month navigation
    calendarEl.addEventListener('click', (e) => {
      if (e.target.closest('[data-ff-dts-calendar-prev]')) {
        navigateMonth(-1);
      } else if (e.target.closest('[data-ff-dts-calendar-next]')) {
        navigateMonth(1);
      } else if (e.target.closest('.ff-dts__calendar-day:not(.ff-dts__calendar-day--disabled)')) {
        const dayEl = e.target.closest('.ff-dts__calendar-day');
        selectDay(dayEl);
      }
    });
  }

  function renderCalendar() {
    const calendarEl = document.querySelector('[data-ff-dts-calendar]');
    if (!calendarEl) return;

    const { calendarMonth, calendarYear } = state;
    const locale = document.documentElement.lang || 'es';
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Month name (capitalized)
    const monthFormatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
    const monthName = monthFormatter.format(new Date(calendarYear, calendarMonth, 1));
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);

    // Day names (short)
    const dayFormatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    const dayNames = [];
    for (let i = 0; i < 7; i++) {
      // Start from Monday (day index 1)
      const date = new Date(2024, 0, i + 1); // 2024-01-01 is a Monday
      const dayName = dayFormatter.format(date);
      dayNames.push(dayName.charAt(0).toUpperCase() + dayName.slice(1));
    }

    // First day of month and total days
    const firstDay = new Date(calendarYear, calendarMonth, 1);
    const lastDay = new Date(calendarYear, calendarMonth + 1, 0);
    const totalDays = lastDay.getDate();

    // Day of week offset (Monday = 0)
    let startOffset = firstDay.getDay() - 1;
    if (startOffset < 0) startOffset = 6; // Sunday wraps to 6

    // Can navigate back?
    const isCurrentMonth = calendarYear === today.getFullYear() && calendarMonth === today.getMonth();

    // Build day cells
    let dayCells = '';

    // Previous month trailing days
    const prevMonthLastDay = new Date(calendarYear, calendarMonth, 0).getDate();
    for (let i = startOffset - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      dayCells += `<div class="ff-dts__calendar-day ff-dts__calendar-day--outside ff-dts__calendar-day--disabled">${day}</div>`;
    }

    // Current month days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(calendarYear, calendarMonth, day);
      date.setHours(0, 0, 0, 0);

      const isPast = date < today;
      const isToday = date.getTime() === today.getTime();
      const dateStr = formatDateISO(calendarYear, calendarMonth + 1, day);
      const isSelected = state.selectedDate === dateStr;

      let classes = 'ff-dts__calendar-day';
      if (isPast) classes += ' ff-dts__calendar-day--disabled';
      if (isToday) classes += ' ff-dts__calendar-day--today';
      if (isSelected) classes += ' ff-dts__calendar-day--selected';

      dayCells += `<div class="${classes}" data-date="${escapeAttr(dateStr)}">${day}</div>`;
    }

    // Next month leading days to complete the last week
    const totalCells = startOffset + totalDays;
    const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
    for (let i = 1; i <= remaining; i++) {
      dayCells += `<div class="ff-dts__calendar-day ff-dts__calendar-day--outside ff-dts__calendar-day--disabled">${i}</div>`;
    }

    calendarEl.innerHTML = `
      <div class="ff-dts__calendar-header">
        <button
          class="ff-dts__calendar-nav"
          type="button"
          data-ff-dts-calendar-prev
          ${isCurrentMonth ? 'disabled' : ''}
          aria-label="Previous month"
        >&lt;</button>
        <span class="ff-dts__calendar-month">${escapeHTML(capitalizedMonth)}</span>
        <button
          class="ff-dts__calendar-nav"
          type="button"
          data-ff-dts-calendar-next
          aria-label="Next month"
        >&gt;</button>
      </div>
      <div class="ff-dts__calendar-grid">
        ${dayNames.map((d) => `<div class="ff-dts__calendar-day-name">${escapeHTML(d)}</div>`).join('')}
        ${dayCells}
      </div>
    `;
  }

  function navigateMonth(direction) {
    state.calendarMonth += direction;
    if (state.calendarMonth > 11) {
      state.calendarMonth = 0;
      state.calendarYear += 1;
    } else if (state.calendarMonth < 0) {
      state.calendarMonth = 11;
      state.calendarYear -= 1;
    }

    // No navegar antes del mes actual
    const today = new Date();
    if (state.calendarYear < today.getFullYear()
      || (state.calendarYear === today.getFullYear() && state.calendarMonth < today.getMonth())) {
      state.calendarMonth = today.getMonth();
      state.calendarYear = today.getFullYear();
    }

    renderCalendar();
  }

  function selectDay(dayEl) {
    const calendarEl = document.querySelector('[data-ff-dts-calendar]');
    if (!calendarEl) return;

    // Remove previous selection
    const prev = calendarEl.querySelector('.ff-dts__calendar-day--selected');
    if (prev) prev.classList.remove('ff-dts__calendar-day--selected');

    // Set new selection
    dayEl.classList.add('ff-dts__calendar-day--selected');
    state.selectedDate = dayEl.dataset.date;

    // Enable confirm button
    const confirmBtn = document.querySelector('[data-ff-dts-confirm="2"]');
    if (confirmBtn) confirmBtn.disabled = false;
  }

  function formatDateISO(year, month, day) {
    const m = String(month).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    return `${year}-${m}-${d}`;
  }

  function formatDateDisplay(isoDate) {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${day}-${month}-${year}`;
  }

  // ── Steps Navigation ─────────────────────────────────────────────────────

  function initSteps() {
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return;

    // Confirm step 2 (calendar)
    container.addEventListener('click', (e) => {
      const confirmBtn = e.target.closest('[data-ff-dts-confirm="2"]');
      if (confirmBtn && state.selectedDate) {
        completeStep(2);
        return;
      }

      const modifyBtn1 = e.target.closest('[data-ff-dts-modify="1"]');
      if (modifyBtn1) {
        editStep(1);
        return;
      }

      const modifyBtn2 = e.target.closest('[data-ff-dts-modify="2"]');
      if (modifyBtn2) {
        editStep(2);
        return;
      }

      const submitBtn = e.target.closest('[data-ff-dts-submit]');
      if (submitBtn) {
        handleSubmit();
        return;
      }
    });

    // Set initial step state
    goToStep(1);
  }

  function goToStep(stepNum) {
    state.currentStep = stepNum;
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return;

    for (let i = 1; i <= 3; i++) {
      const stepEl = container.querySelector(`[data-ff-dts-step="${i}"]`);
      if (!stepEl) continue;

      const contentEl = stepEl.querySelector('[data-ff-dts-step-content]');
      const summaryEl = stepEl.querySelector('[data-ff-dts-step-summary]');

      if (i === stepNum) {
        // Active step
        stepEl.classList.add('ff-dts__step--active');
        stepEl.classList.remove('ff-dts__step--completed', 'ff-dts__step--disabled');
        if (contentEl) contentEl.style.display = '';
        if (summaryEl) summaryEl.style.display = 'none';
      } else if (i < stepNum) {
        // Completed steps stay as completed
        stepEl.classList.add('ff-dts__step--completed');
        stepEl.classList.remove('ff-dts__step--active', 'ff-dts__step--disabled');
        if (contentEl) contentEl.style.display = 'none';
        if (summaryEl) summaryEl.style.display = '';
      } else {
        // Future steps are disabled
        stepEl.classList.add('ff-dts__step--disabled');
        stepEl.classList.remove('ff-dts__step--active', 'ff-dts__step--completed');
        if (contentEl) contentEl.style.display = 'none';
        if (summaryEl) summaryEl.style.display = 'none';
      }
    }
  }

  function completeStep(stepNum) {
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return;

    const stepEl = container.querySelector(`[data-ff-dts-step="${stepNum}"]`);
    if (!stepEl) return;

    // Update summary with selected data
    const summaryEl = stepEl.querySelector('[data-ff-dts-step-summary]');
    if (summaryEl) {
      if (stepNum === 1 && state.selectedStore) {
        summaryEl.innerHTML = `
          <div class="ff-dts__step-summary-content">
            <div class="ff-dts__step-summary-info">
              <p class="ff-dts__step-summary-title ff-dts__store-card-name ff-snippet-text">${escapeHTML(state.selectedStore.name)}</p>
              <p class="ff-dts__step-summary-detail ff-dts__store-card-address ff-snippet-text">
                ${escapeHTML(state.selectedStore.street)}, ${escapeHTML(state.selectedStore.zip)} ${escapeHTML(state.selectedStore.city)}
              </p>
            </div>
            <button class="ff-snippet-button ff-dts__step-modify" type="button" data-ff-dts-modify="1">
              <span class="ff-button-text">
                <div class="ff-dts__step-modify-text ff-snippet-text">
                  ${escapeHTML(getTranslation('modify'))}
                </div>
              </span>
            </button>
          </div>
        `;
      } else if (stepNum === 2 && state.selectedDate) {
        summaryEl.innerHTML = `
          <div class="ff-dts__step-summary-content">
            <div class="ff-dts__step-summary-info">
              <p class="ff-dts__step-summary-title ff-dts__store-card-name ff-snippet-text">${escapeHTML(formatDateDisplay(state.selectedDate))}</p>
            </div>
            <button class="ff-snippet-button ff-dts__step-modify" type="button" data-ff-dts-modify="2">
              <span class="ff-button-text">
                <div class="ff-dts__step-modify-text ff-snippet-text">
                  ${escapeHTML(getTranslation('modify'))}
                </div>
              </span>
            </button>
          </div>
        `;
      }
    }

    // Go to next step
    const nextStep = stepNum + 1;
    if (nextStep <= 3) {
      goToStep(nextStep);
    }
  }

  function editStep(stepNum) {
    // Reset steps after the one being edited
    if (stepNum <= 1) {
      state.selectedStore = null;
      const storeCard = document.querySelector('[data-ff-dts-store-card]');
      if (storeCard) {
        storeCard.classList.remove('is-open');
        storeCard.innerHTML = '';
      }
    }
    if (stepNum <= 2) {
      state.selectedDate = null;
      const confirmBtn = document.querySelector('[data-ff-dts-confirm="2"]');
      if (confirmBtn) confirmBtn.disabled = true;
    }

    goToStep(stepNum);

    // Re-render calendar to clear selection
    if (stepNum <= 2) {
      renderCalendar();
    }
  }

  // ── Form (Step 3) ────────────────────────────────────────────────────────

  function initForm() {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return;

    // Prevent native submission
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      handleSubmit();
    });
  }

  function validateForm() {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return { valid: false, errors: [] };

    const errors = [];

    const firstName = form.querySelector('[name="firstName"]');
    const lastName = form.querySelector('[name="lastName"]');
    const email = form.querySelector('[name="email"]');
    const phone = form.querySelector('[name="phone"]');
    const privacyPolicy = form.querySelector('[name="privacyPolicyAccepted"]');

    // Required fields
    if (!firstName || !firstName.value.trim()) {
      errors.push({ field: 'firstName', message: getTranslation('errorRequired') });
    }
    if (!lastName || !lastName.value.trim()) {
      errors.push({ field: 'lastName', message: getTranslation('errorRequired') });
    }
    if (!email || !email.value.trim()) {
      errors.push({ field: 'email', message: getTranslation('errorRequired') });
    } else if (!isValidEmail(email.value.trim())) {
      errors.push({ field: 'email', message: getTranslation('errorEmail') });
    }
    if (!privacyPolicy || !privacyPolicy.checked) {
      errors.push({ field: 'privacyPolicyAccepted', message: getTranslation('errorPrivacy') });
    }

    return { valid: errors.length === 0, errors };
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  function getFormData() {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return null;

    return {
      firstName: (form.querySelector('[name="firstName"]')?.value || '').trim(),
      lastName: (form.querySelector('[name="lastName"]')?.value || '').trim(),
      email: (form.querySelector('[name="email"]')?.value || '').trim(),
      phone: (form.querySelector('[name="phone"]')?.value || '').trim(),
      newsletterConsent: form.querySelector('[name="newsletterConsent"]')?.checked || false,
      privacyPolicyAccepted: form.querySelector('[name="privacyPolicyAccepted"]')?.checked || false,
    };
  }

  function showFormErrors(errors) {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return;

    // Clear previous errors
    form.querySelectorAll('.ff-dts__form-error').forEach((el) => el.remove());
    form.querySelectorAll('.ff-dts__form-field--error').forEach((el) => {
      el.classList.remove('ff-dts__form-field--error');
    });

    errors.forEach(({ field, message }) => {
      const input = form.querySelector(`[name="${CSS.escape(field)}"]`);
      if (!input) return;

      const fieldWrapper = input.closest('.ff-dts__form-field');
      if (fieldWrapper) fieldWrapper.classList.add('ff-dts__form-field--error');

      const errorEl = document.createElement('span');
      errorEl.className = 'ff-dts__form-error';
      errorEl.textContent = message;

      const isCheckable = input.type === 'checkbox' || input.type === 'radio';
      const anchor = isCheckable ? (input.closest('.ff-dts__checkbox-label') || input) : input;
      anchor.parentNode.insertBefore(errorEl, anchor.nextSibling);
    });
  }

  function clearFormErrors() {
    const form = document.querySelector('[data-ff-dts-form]');
    if (!form) return;

    form.querySelectorAll('.ff-dts__form-error').forEach((el) => el.remove());
    form.querySelectorAll('.ff-dts__form-field--error').forEach((el) => {
      el.classList.remove('ff-dts__form-field--error');
    });
  }

  async function handleSubmit() {
    clearFormErrors();

    // Validate state
    if (!state.selectedStore) {
      console.error('[DTS] No store selected');
      return;
    }
    if (!state.selectedDate) {
      console.error('[DTS] No date selected');
      return;
    }

    const { valid, errors } = validateForm();
    if (!valid) {
      showFormErrors(errors);
      return;
    }

    const formData = getFormData();
    const payload = buildDTSPayload(formData);
    const submitBtn = document.querySelector('[data-ff-dts-submit]');
    const messageEl = document.querySelector('[data-ff-dts-message]');

    // Loading state
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.classList.add('ff-dts__submit--loading');
    }
    if (messageEl) {
      messageEl.innerHTML = '';
      messageEl.style.display = 'none';
    }

    try {
      await submitDTSRequest(payload);
      showSuccessPopup();
    } catch (err) {
      console.error('[DTS] Submission error:', err);

      if (messageEl) {
        messageEl.innerHTML = `
          <div class="ff-dts__message ff-dts__message--error">
            <p>${escapeHTML(err.message || getTranslation('errorSubmit'))}</p>
          </div>
        `;
        messageEl.style.display = 'block';
      }
    } finally {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('ff-dts__submit--loading');
      }
    }
  }

  // ── Payload Builder ──────────────────────────────────────────────────────

  function buildDTSPayload(formData) {
    const s = state.selectedStore || {};

    const products = state.cartItems.map((item) => ({
      productId: item.product_id,
      variantId: item.id,
      sapid: item.sapid || '',
      title: item.title,
      image: item.imageUrl || '',
      price: item.price || '',
      variantTitle: item.variantTitle || '',
      productUrl: item.productUrl || '',
      productType: item.type,
    }));

    return {
      store: {
        name: s.name || '',
        sapid: s.sap_id || '',
        s4clientepartner: s.partner_id || '',
        address: [s.street, [s.zip, s.city].filter(Boolean).join(' '), s.country].filter(Boolean).join(', '),
        lat: s.lat || null,
        lng: s.lng || null,
      },
      date: state.selectedDate,
      locale: (document.documentElement.lang || 'en').split('-')[0],
      customer: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone || '',
        acceptsMarketing: formData.newsletterConsent,
        // Not part of the reference payload shape, but this form still
        // collects and requires it (GDPR) — kept as an additive field.
        privacyPolicyAccepted: formData.privacyPolicyAccepted,
      },
      products: products,
      product: products[0] || null,
    };
  }

  // ── Success Popup ────────────────────────────────────────────────────────

  function showSuccessPopup() {
    const store = state.selectedStore || {};
    const date = state.selectedDate ? new Date(state.selectedDate) : null;
    const items = state.cartItems || [];

    const refLabel = document.querySelector('[data-ff-dts]')?.dataset.tRefLabel || 'Ref.';

    const storeEl = document.querySelector('[data-ff-dts-success-store]');
    if (storeEl) {
      const addrLine = [store.zip, store.city].filter(Boolean).join(' ');
      storeEl.innerHTML = `
        <p class="ff-dts__store-card-name"><strong>${escapeHTML(store.name || '')}</strong></p>
        ${store.street ? `<p>${escapeHTML(store.street)}</p>` : ''}
        ${addrLine ? `<p>${escapeHTML(addrLine)}${store.country ? `, ${escapeHTML(store.country)}` : ''}</p>` : ''}
      `;
    }

    const dateEl = document.querySelector('[data-ff-dts-success-date]');
    if (dateEl && date) {
      dateEl.textContent = date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }

    const itemsEl = document.querySelector('[data-ff-dts-success-items]');
    if (itemsEl) {
      itemsEl.innerHTML = items.map((item) => `
        <div class="ff-dts__sidebar-item">
          <div class="ff-dts__sidebar-item-image">
            ${item.imageUrl ? `<img src="${escapeAttr(item.imageUrl)}" alt="${escapeAttr(item.title)}" loading="lazy" />` : ''}
          </div>
          <div class="ff-dts__sidebar-item-info">
            <p class="ff-dts__sidebar-item-title">${escapeHTML(item.title)}</p>
            <p class="ff-dts__sidebar-item-ref">${escapeHTML(refLabel)} ${escapeHTML(item.sku)}</p>
          </div>
        </div>
      `).join('');
    }

    document.dispatchEvent(new CustomEvent('ff-popup:open', {
      detail: { popupId: 'ff-dts-success-popup' }
    }));

    wireSuccessCTA();
  }

  let ctaWired = false;
  function wireSuccessCTA() {
    if (ctaWired) return;
    ctaWired = true;
    const cta = document.querySelector('[data-ff-dts-cta]');
    if (!cta) return;
    cta.addEventListener('click', async (e) => {
      e.preventDefault();
      cta.style.pointerEvents = 'none';
      cta.style.opacity = '0.6';
      try {
        // Clear cart so a returning user starts a fresh DTS flow.
        await fetch('/cart/clear.js', { method: 'POST' });
      } catch (err) {
        console.error('[DTS] Could not clear cart:', err);
      }
      window.location.href = cta.getAttribute('href') || '/';
    });
  }

  // ── API Functions ────────────────────────────────────────────────────────

  async function submitDTSRequest(payload) {
    if (DTS_MOCK_MODE) {
      return mockSubmitDTSRequest(payload);
    }

    const endpoint = document.querySelector('[data-ff-dts]')?.dataset.dtsEndpoint || '/apps/etniaAppSync/dts-form';

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (res.status !== 202) {
      let message = 'Failed to submit';
      try {
        const errorData = await res.json();
        message = errorData.error || message;
      } catch (e) {}
      throw new Error(message);
    }

    // 202 Accepted — success. Body may be empty/non-JSON; parse best-effort.
    try {
      return await res.json();
    } catch (e) {
      return {};
    }
  }

  // ── Translations ─────────────────────────────────────────────────────────

  function getTranslation(key) {
    const container = document.querySelector('[data-ff-dts]');
    if (!container) return key;

    const translations = {
      confirmStore: container.dataset.tStep1Confirm || 'CONFIRM STORE',
      confirmDate: container.dataset.tStep2Confirm || 'CONFIRM DATE',
      modify: container.dataset.tModify || 'Modify',
      submit: container.dataset.tStep3Submit || 'FINALIZE BOOKING',
      successTitle: container.dataset.tSuccessTitle || 'Request sent!',
      successMessage: container.dataset.tSuccessMessage || 'You will receive a confirmation email shortly.',
      errorSubmit: container.dataset.tErrorSubmit || 'Could not send the request. Please try again.',
      errorRequired: container.dataset.tErrorRequired || 'Please complete all required fields.',
      errorEmail: container.dataset.tErrorEmail || 'Please enter a valid email address.',
      errorPrivacy: container.dataset.tErrorPrivacy || 'You must accept the privacy policy.',
      errorNoStore: container.dataset.tErrorNoStore || 'Please select a store.',
      errorNoDate: container.dataset.tErrorNoDate || 'Please select a trial date.',
    };

    return translations[key] || key;
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  function escapeHTML(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  function escapeAttr(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Mock data ────────────────────────────────────────────────────────────

  function mockSubmitDTSRequest(payload) {
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('[DTS] Mock submit payload:', payload);
        resolve({
          success: true,
          message: 'Your Drive to Store appointment has been confirmed. You will receive a confirmation email shortly.',
          confirmationId: 'DTS-' + Date.now(),
        });
      }, 800);
    });
  }
})();
